import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Loader2, Trophy, ChevronLeft, ChevronRight, Crown } from 'lucide-react';
import TierBadge from '@/components/TierBadge';
import { shouldAutoRefresh } from '@/lib/marketHoursPolling';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * TournamentTab — Round 84 (Feb 14, 2026).
 *
 * Quarterly Tournament mode. Each frozen week of the quarter awards
 * podium points (1st = 10 pts, 2nd = 7, 3rd = 5, 4-10 = 3, 11+ = 1).
 * Top of the quarter is whoever consistently lands podiums across
 * all four rotating themes — not just the user who happened to nail
 * the Total Return week.
 */
function currentQuarterLabel() {
  const d = new Date();
  const q = Math.floor(d.getMonth() / 3) + 1;
  return `${d.getFullYear()}-Q${q}`;
}

function shiftQuarter(label, delta) {
  const [year, q] = label.split('-Q').map(Number);
  let nq = q + delta;
  let ny = year;
  while (nq < 1) { nq += 4; ny -= 1; }
  while (nq > 4) { nq -= 4; ny += 1; }
  return `${ny}-Q${nq}`;
}

function StandingRow({ row, rank, isMe }) {
  const podiumEmoji = rank === 1 ? '🥇' : rank === 2 ? '🥈' : rank === 3 ? '🥉' : null;
  return (
    <tr className={isMe ? 'bg-emerald-50 dark:bg-emerald-950/30 font-bold' : ''} data-testid={`tournament-row-${row.handle}`}>
      <td className="px-2 py-2 text-center w-12">
        <span className="font-bold tabular-nums">{podiumEmoji || `#${rank}`}</span>
      </td>
      <td className="px-2 py-2">
        <div className="flex items-center gap-1.5">
          <span className="font-semibold">@{row.handle}</span>
          {row.tier ? <TierBadge tier={row.tier} showLabel={false} /> : null}
        </div>
      </td>
      <td className="px-2 py-2 text-right tabular-nums font-bold text-violet-600 dark:text-violet-400">
        {row.total_points}
      </td>
      <td className="px-2 py-2 text-right tabular-nums hidden sm:table-cell">
        {row.weeks_played}
      </td>
      <td className="px-2 py-2 text-right tabular-nums hidden sm:table-cell">
        {row.wins || 0}🏆
      </td>
    </tr>
  );
}

export default function TournamentTab({ myHandle }) {
  const [quarter, setQuarter] = useState(currentQuarterLabel());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStandings = useCallback((silent = false) => {
    if (!silent) setLoading(true);
    return axios
      .get(`${API}/community/tournament/standings`, { params: { quarter, limit: 25 } })
      .then((r) => setData(r.data))
      .catch(() => { if (!silent) setData(null); })
      .finally(() => { if (!silent) setLoading(false); });
  }, [quarter]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${API}/community/tournament/standings`, { params: { quarter, limit: 25 } })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [quarter]);

  // Round 84k Phase 3.1 — auto-refresh tournament standings every 5
  // minutes so wins / scores stay current as weekly leaderboards
  // resolve. Skipped automatically when viewing a past quarter (its
  // results are immutable once the quarter closes).
  //
  // Round 84k Phase 3.2 — gated to production hostname AND 9:30 AM →
  // 4:15 PM ET market window so the preview environment never polls.
  useEffect(() => {
    const cur = currentQuarterLabel();
    if (quarter !== cur) return undefined;
    if (!shouldAutoRefresh()) return undefined;
    const id = setInterval(() => { fetchStandings(true); }, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [quarter, fetchStandings]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground" data-testid="tournament-loading">
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        Loading tournament…
      </div>
    );
  }

  const rows = data?.rows || [];

  return (
    <div data-testid="tournament-tab">
      {/* Header */}
      <div className="rounded-2xl border border-violet-300 dark:border-violet-700 bg-gradient-to-br from-violet-50 to-card dark:from-violet-950/30 dark:to-card p-4 mb-4">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl bg-violet-100 dark:bg-violet-950/40 flex items-center justify-center">
            <Trophy className="w-5 h-5 text-violet-600 dark:text-violet-400" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-base sm:text-lg font-bold">Quarterly Tournament</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Every weekly podium awards points: 1st = 10 · 2nd = 7 · 3rd = 5 · 4–10 = 3 · 11+ = 1.
              <br className="hidden sm:block" />
              Top of the quarter is whoever earns the most across all four rotating themes.
            </p>
          </div>
        </div>
      </div>

      {/* Quarter selector */}
      <div className="flex items-center justify-between mb-3">
        <button
          type="button"
          className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
          onClick={() => setQuarter(shiftQuarter(quarter, -1))}
          data-testid="tournament-prev-quarter"
          title="Previous quarter"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <div className="flex items-center gap-2 text-sm font-bold" data-testid="tournament-quarter-label">
          <Trophy className="w-4 h-4 text-amber-500" />
          {quarter}
          {data?.is_current ? (
            <span className="text-[10px] uppercase tracking-wide font-bold px-1.5 py-0.5 rounded bg-emerald-100 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-400">Live</span>
          ) : null}
        </div>
        <button
          type="button"
          className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
          onClick={() => setQuarter(shiftQuarter(quarter, 1))}
          disabled={quarter === data?.current_quarter}
          data-testid="tournament-next-quarter"
          title="Next quarter"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      {rows.length === 0 ? (
        <div className="rounded-2xl border-2 border-dashed border-border bg-muted/20 p-8 text-center" data-testid="tournament-empty">
          <Crown className="w-8 h-8 text-muted-foreground/40 mx-auto mb-2" />
          <h3 className="text-sm font-bold mb-1">No standings yet</h3>
          <p className="text-xs text-muted-foreground">
            {data?.weeks_counted === 0
              ? 'No leaderboard weeks have been played in this quarter yet. Enter the weekly leaderboard to start earning tournament points.'
              : 'No entries on the weekly leaderboard this quarter.'}
          </p>
        </div>
      ) : (
        <div className="rounded-2xl border border-border bg-card overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-muted/40">
              <tr className="border-b border-border">
                <th className="px-2 py-2 text-left font-semibold">Rank</th>
                <th className="px-2 py-2 text-left font-semibold">Member</th>
                <th className="px-2 py-2 text-right font-semibold">Points</th>
                <th className="px-2 py-2 text-right font-semibold hidden sm:table-cell">Weeks</th>
                <th className="px-2 py-2 text-right font-semibold hidden sm:table-cell">Wins</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => (
                <StandingRow key={row.handle} row={row} rank={idx + 1} isMe={myHandle && row.handle === myHandle} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data?.weeks_counted > 0 ? (
        <p className="text-[11px] text-muted-foreground text-center mt-3">
          Counting {data.weeks_counted} {data.weeks_counted === 1 ? 'week' : 'weeks'} so far this quarter.
        </p>
      ) : null}
    </div>
  );
}
